import { KeyboardEvent, useEffect, useMemo, useState } from 'react'
import { useModal } from '../../../common/components/Modal'
import { useSetupModalFocusTrap } from '../../../common/components/Modal/useAsyncPrompt'
import modalStyles from '../../../common/components/Modal/styles.module.scss'
import { fetchHipsProperties, normalizeHipsUrl } from './HipsProperties'
import styles from './style.module.scss'

type Validation = {
  url: string
  status: 'checking' | 'valid' | 'error'
  title?: string
  message?: string
}

type Props = {
  onClose: () => void
  onSelect: (baseUrl: string) => void
}

export function HipsCustomUrlModal({ onClose, onSelect }: Props) {
  const [input, setInput] = useState('')
  const [validation, setValidation] = useState<Validation>()
  const baseUrl = useMemo(() => normalizeHipsUrl(input), [input])

  const { blockRef } = useModal()
  useSetupModalFocusTrap(blockRef)

  useEffect(() => {
    if (!baseUrl) {
      return
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => {
      setValidation({ url: baseUrl, status: 'checking' })
      fetchHipsProperties(baseUrl, controller.signal)
        .then(properties => {
          if (controller.signal.aborted) return
          setValidation({
            url: baseUrl,
            status: 'valid',
            title: properties.cards.find(card => card.key === 'obs_title')?.value,
          })
        })
        .catch(error => {
          if (controller.signal.aborted) return
          setValidation({ url: baseUrl, status: 'error', message: String(error) })
        })
    }, 350)

    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [baseUrl])

  const isValid = !!baseUrl && validation?.url === baseUrl && validation.status === 'valid'

  const apply = () => {
    if (baseUrl && isValid) {
      onSelect(baseUrl)
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      event.preventDefault()
      onClose()
    }
  }

  return (
    <div className={modalStyles.prompt} tabIndex={-1} onKeyDown={onKeyDown}>
      <div className={modalStyles.message}>HiPS URL</div>
      <form
        className={styles.hipsUrlForm}
        onSubmit={event => {
          event.preventDefault()
          apply()
        }}
      >
        <input
          type="text"
          aria-label="HiPS URL"
          className={styles.hipsUrlInput}
          autoFocus
          spellCheck={false}
          placeholder="https://example.org/hips"
          value={input}
          onChange={event => setInput(event.currentTarget.value)}
        />
        <div className={styles.hipsUrlStatus} aria-live="polite">
          {input.trim() && !baseUrl && <span className={styles.hipsUrlError}>http(s) の HiPS URL を入力してください。</span>}
          {baseUrl && validation?.url === baseUrl && validation.status === 'checking' && 'HiPS 情報を取得中…'}
          {baseUrl && validation?.url === baseUrl && validation.status === 'valid' && (validation.title ?? 'HiPS 情報を取得しました。')}
          {baseUrl && validation?.url === baseUrl && validation.status === 'error' && <span className={styles.hipsUrlError}>取得に失敗しました: {validation.message}</span>}
        </div>
        <div className={styles.hipsUrlActions}>
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={!isValid}>OK</button>
        </div>
      </form>
    </div>
  )
}
